import React from "react";

export const discColor = {
  Dimensional: "b-blue", Pressure: "b-amber", Torque: "b-violet",
  Temperature: "b-red", Electronics: "b-green", Hydraulics: "b-slate",
  Mass: "b-gold", Flow: "b-blue",
};

export const STATUS = {
  received: ["Received", "b-slate"],
  assigned: ["Assigned", "b-slate"],
  in_progress: ["In Progress", "b-blue"],
  calibrated: ["Calibrated", "b-green"],
  adjusted: ["Adjusted", "b-green"],
  repaired: ["Repaired", "b-green"],
  failed_calibration: ["Failed", "b-red"],
  scrapped: ["Unserviceable", "b-red"],
  unserviceable_review: ["Unserviceable Review", "b-amber"],
  returned_without_work: ["Returned (no work)", "b-amber"],
  unable_to_calibrate: ["Unable to Cal.", "b-red"],
  parts_required: ["Parts Required", "b-amber"],
  awaiting_spares: ["Awaiting Spares", "b-amber"],
  awaiting_customer_approval: ["Awaiting Customer", "b-amber"],
  awaiting_quote: ["Awaiting Quote", "b-amber"],
  awaiting_subcontractor: ["Awaiting Subcon.", "b-violet"],
  at_subcontractor: ["At Subcontractor", "b-violet"],
  returned_from_subcontractor: ["Returned (Sub)", "b-violet"],
  ready_for_admin: ["Ready for Admin", "b-blue"],
  ready_for_delivery: ["In Dispatch", "b-green"],
  dispatched: ["Ready for Collection", "b-slate"],
  collected: ["Collected", "b-slate"],
  delivered: ["Delivered", "b-slate"],
  closed: ["Closed", "b-slate"],
  paid: ["Paid", "b-gold"],
};

const PRIORITY = {
  normal: ["Normal", "b-slate"],
  high:   ["High",   "b-amber"],
  urgent: ["Urgent", "b-red"],
};

const PORTAL = {
  active: ["Active", "b-green"], due_soon: ["Due Soon", "b-amber"],
  overdue: ["Overdue", "b-red"], in_lab: ["In Lab", "b-blue"],
  dispatched: ["Dispatched", "b-slate"], out_of_service: ["Unserviceable", "b-red"],
  ready_for_admin: ["Admin / Updating", "b-amber"],
  ready_for_delivery: ["Ready for Dispatch", "b-blue"],
  collected: ["Collected", "b-green"], delivered: ["Delivered", "b-green"],
  quote_declined: ["Quote Declined", "b-red"],
};

const PAYMENT = {
  not_invoiced: ["Not invoiced", "b-slate"],
  invoiced: ["Invoiced", "b-amber"],
  awaiting_payment: ["Awaiting Payment", "b-red"],
  paid: ["Paid", "b-gold"],
  not_required: ["Not required", "b-slate"],
};

export function Badge({ label, cls, testid }) {
  return (
    <span className={`badge ${cls}`} data-testid={testid}>
      <span className="bd" />
      {label}
    </span>
  );
}

export function StatusBadge({ status, testid }) {
  const [label, cls] = STATUS[status] || [status || "—", "b-slate"];
  return <Badge label={label} cls={cls} testid={testid} />;
}

export function PriorityBadge({ priority, testid }) {
  if (!priority || priority === "normal") return null;
  const [label, cls] = PRIORITY[priority] || [priority, "b-slate"];
  return <Badge label={label} cls={cls} testid={testid} />;
}

export function PortalBadge({ status, testid, labelOverrides }) {
  const map = labelOverrides ? { ...PORTAL, ...labelOverrides } : PORTAL;
  const [label, cls] = map[status] || [status || "—", "b-slate"];
  return <Badge label={label} cls={cls} testid={testid} />;
}

export function PaymentBadge({ status, testid }) {
  const [label, cls] = PAYMENT[status || "not_invoiced"] || [status || "—", "b-slate"];
  return <Badge label={label} cls={cls} testid={testid} />;
}

export function DisciplineBadge({ name, testid }) {
  if (!name) return <span className="t-mut">—</span>;
  return <Badge label={name} cls={discColor[name] || "b-slate"} testid={testid} />;
}

// Statuses meaning the item has physically left PTE's control (mirrors the
// backend's HANDED_OVER_STATUSES). A line only fully CLOSES (and drops back
// to the Equipment Register) once it is handed over AND payment is satisfied.
const HANDED_OVER = ["dispatched", "collected", "delivered"];

// "What's still needed" for a Dispatch line — returns an ordered ARRAY of
// short hints. For a not-yet-closed line: what's needed to reach Closed. For a
// CLOSED line: what's needed to fully complete it and drop it off the Closed
// queue (mark Paid, then fill Quote / PO / Invoice — 'N/A' counts as filled).
export function closeBlocker(status, paymentStatus, quote, po, invoice) {
  const ps = paymentStatus || "";
  const blank = (v) => !String(v ?? "").trim();
  if (status === "closed") {
    const h = [];
    if (!["paid", "not_required"].includes(ps)) h.push("Mark as Paid");
    if (blank(quote)) h.push("Add Quote #");
    if (blank(po)) h.push("Add PO #");
    if (blank(invoice)) h.push("Add Invoice #");
    return h;
  }
  const needsHandover = !HANDED_OVER.includes(status);
  const needsPayment = !["paid", "not_required"].includes(ps);
  if (needsHandover && needsPayment) return ["Dispatch / hand over, then mark Paid"];
  if (needsHandover) return ["Dispatch / hand over to customer"];
  if (needsPayment) return ["Mark as Paid (or Not required)"];
  return [];
}

// One small amber chip for a single hint string.
export function CloseHint({ hint, testid }) {
  if (!hint) return null;
  return (
    <span className="badge b-amber" style={{ fontSize: 10.5, whiteSpace: "nowrap" }}
      title={`Still needed: ${hint}`} data-testid={testid}>
      <span className="bd" /> {hint}
    </span>
  );
}

// Renders the full array of hints for a line as a wrapped chip group (or a
// muted dash when the line needs nothing / is fully complete).
export function CloseHints({ status, paymentStatus, quote, po, invoice, testidPrefix }) {
  const list = closeBlocker(status, paymentStatus, quote, po, invoice);
  if (!list.length) return <span className="t-mut" title="Fully complete">—</span>;
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}
      data-testid={testidPrefix ? `${testidPrefix}-group` : undefined}>
      {list.map((h) => <CloseHint key={h} hint={h} testid={testidPrefix ? `${testidPrefix}-${h.replace(/[^a-z]+/gi, "-").toLowerCase()}` : undefined} />)}
    </div>
  );
}


